/**
 * New business grouper signals.
 *
 * A deal is described to the grouper as a flat set of strings: the
 * opportunity's TRUE dts_* solution toggles (by field name) plus whatever the
 * close activity's Products Sold says (by label, as Dataverse formats it).
 * The browser has the toggles only as productsToQuote labels and the Function
 * has the raw entity, so both reduce to this one vocabulary and the admin's
 * bucket rules in sales.newBusiness mean the same thing everywhere.
 *
 * Prod probe 9/27: 94 of 182 closes carry no Products Sold, and 181 of 182
 * opportunities carry at least one toggle, so the toggles are the primary
 * signal and the sold labels are supplementary.
 */

export const OPP_TOGGLE_FIELDS = [
  'dts_tpaadmin',
  'dts_veracityrx',
  'dts_maplebridge',
  'dts_minimumessentialcoveragemec',
  'dts_veracityonsite',
  'dts_veracityrisk',
  'dts_providersofexcellence',
] as const;

export type OppToggleField = (typeof OPP_TOGGLE_FIELDS)[number];

/** The labels `Opportunity.productsToQuote` carries for each toggle. */
export const OPP_TOGGLE_LABELS: Record<OppToggleField, string> = {
  dts_tpaadmin: 'TPA Admin',
  dts_veracityrx: 'Veracity Rx',
  dts_maplebridge: 'Maplebridge',
  dts_minimumessentialcoveragemec: 'MEC',
  dts_veracityonsite: 'Veracity OnSite',
  dts_veracityrisk: 'Veracity Risk',
  dts_providersofexcellence: 'Providers of Excellence',
};

/** Case-insensitive, whitespace-collapsed key used for every comparison. */
export function normalizeSignal(s: string): string {
  return s.trim().replace(/\s+/g, ' ').toLowerCase();
}

const FIELD_BY_LABEL = new Map<string, OppToggleField>(
  (Object.entries(OPP_TOGGLE_LABELS) as [OppToggleField, string][]).map(([f, l]) => [normalizeSignal(l), f]),
);

export function toggleFieldForLabel(label: string): OppToggleField | undefined {
  return FIELD_BY_LABEL.get(normalizeSignal(label));
}

/**
 * Split a Products Sold formatted value. Dataverse joins multi-select choice
 * labels with "; " (prod: "TPA Services ; VeracityRx Solutions"); a comma is
 * accepted too for hand-typed values.
 */
export function splitSoldLabels(sold: string | null | undefined): string[] {
  if (!sold) return [];
  return sold
    .split(/[;,]/)
    .map((s) => s.trim().replace(/\s+/g, ' '))
    .filter(Boolean);
}

export interface BuildSignalsInput {
  /** Raw dts_* field names that are true on the opportunity (Function side). */
  toggleFields?: readonly string[];
  /** `Opportunity.productsToQuote` labels (browser side). */
  productLabels?: readonly string[];
  /** Already-split sold labels. */
  soldLabels?: readonly string[];
  /** The close activity's formatted Products Sold, split here. */
  productsSold?: string | null;
}

/**
 * Reduce a deal to its signal set: toggle field names plus sold labels,
 * de-duplicated case-insensitively, sorted so two deals with the same
 * signals serialize identically.
 */
export function buildSignals(input: BuildSignalsInput): string[] {
  const byKey = new Map<string, string>();
  const add = (raw: string) => {
    const trimmed = raw.trim().replace(/\s+/g, ' ');
    if (!trimmed) return;
    const key = normalizeSignal(trimmed);
    if (!byKey.has(key)) byKey.set(key, trimmed);
  };
  for (const f of input.toggleFields ?? []) add(f.toLowerCase());
  for (const l of input.productLabels ?? []) {
    const field = toggleFieldForLabel(l);
    add(field ?? l);
  }
  for (const l of input.soldLabels ?? []) add(l);
  for (const l of splitSoldLabels(input.productsSold)) add(l);
  return [...byKey.values()].sort((a, b) => a.localeCompare(b));
}
